import React from 'react';
import {Image, StyleSheet, View} from 'react-native';

import {resolveWeight} from '../../../design-system/tokens';
import theme from '../../../style/theme';
import {rewardsMarketingName} from '../../../utils/rewards';
import Text from '../../themed/Text';
import CheckBox, {CheckBoxProps} from '../CheckBox';
import {REWARDS_BANNER_ASPECT_RATIO, rewardsBranding} from '../rewardsBranding';

type OptInProps = Pick<CheckBoxProps, 'onChange' | 'checked'>;

/**
 * Rewards opt-in banner (DIS-1731).
 *
 * One rounded card: the brand's rewards banner image (DIS-1765), directly
 * above a pale panel holding the opt-in checkbox and its supporting copy. The loyalty terms
 * sentence is NOT here - it sits under the account T&Cs on the screen.
 *
 * `onChange` is passed straight through. Do not wrap or negate it here - the
 * call site owns that (see app/components/ui-controls/web/CheckBox.tsx).
 */
export default function OptIn({checked, onChange}: OptInProps) {
  return (
    <View style={styles.container} testID="opt-in-container">
      <Image
        testID="banner-header"
        source={rewardsBranding.banner}
        style={styles.banner}
        resizeMode="cover"
        accessibilityIgnoresInvertColors
      />
      <View
        style={[styles.panel, {backgroundColor: rewardsBranding.optInSurface}]}
        testID="content-container">
        {/* The subcopy is passed through the checkbox rather than rendered as a
            sibling below it, so that it shares the label's left edge instead of
            starting back at the panel's. The box sits beside the first line of
            the column. */}
        <CheckBox
          testID="opt-in-checkbox"
          checked={checked}
          onChange={onChange}
          text={
            <View style={styles.textContainer} testID="text-container">
              <Text variant="body2" style={styles.optInLabel}>
                {`Yes! I want ${rewardsMarketingName} discounts`}
              </Text>
              <Text variant="body2" testID="opt-in-subcopy">
                Get access to member pricing, special offers, and instant savings.
              </Text>
            </View>
          }
        />
      </View>
    </View>
  );
}

// SHORTCUT: the 8 corner radius is a literal. The panel surface comes from
// rewardsBranding (DIS-1765: #F5FFE9 on Thirsty Camel, #F6FBFF elsewhere); it
// must NOT be repointed at the per-brand theme. Ceiling: no L0 colour or
// spacing token layer exists. Upgrade path: a design-system colour/spacing
// module under app/design-system/tokens/ - DIS-1737 blocks it, DIS-1735 is the
// epic.
const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  banner: {
    width: '100%',
    height: undefined,
    aspectRatio: REWARDS_BANNER_ASPECT_RATIO,
  },
  panel: {
    paddingVertical: 16,
    paddingHorizontal: 10,
  },
  textContainer: {
    gap: 4,
  },
  // Brand-resolved, not a literal: `text.heading.primary` is LMG_BLUE on
  // Bottlemart and SipnSave and black on Harry Brown and Thirsty Camel, so the
  // label stays legible on the pale panel for all four. The weight goes through
  // resolveWeight because Android silently renders Regular for any numeric
  // fontWeight but 700.
  optInLabel: {
    color: theme.colour.text.heading.primary,
    ...resolveWeight('semiBold'),
  },
});
